/**
 * @fileoverview Reset Password page for setting a new password using a token
 */

import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { LandingNavbar } from '@/components/layout/LandingNavbar';
import { apiService } from '@/services/api/apiService';

export const ResetPasswordPage = () => {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const [passwords, setPasswords] = useState({
        newPassword: '',
        confirmPassword: ''
    });
    const [status, setStatus] = useState('idle'); // 'idle', 'loading', 'success', 'error'
    const [error, setError] = useState('');
    const navigate = useNavigate();
    const { t } = useLanguage();

    useEffect(() => {
        if (!token) {
            setError(t('auth.invalid_token', 'Link de recuperación inválido o expirado.'));
            setStatus('error');
        }
    }, [token, t]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (passwords.newPassword !== passwords.confirmPassword) {
            setError(t('settings.password_mismatch', 'Las contraseñas no coinciden'));
            return;
        }

        if (passwords.newPassword.length < 8) {
            setError(t('auth.password_too_short', 'La contraseña debe tener al menos 8 caracteres'));
            return;
        }

        setStatus('loading');
        setError('');

        try {
            await apiService.request('/auth/reset-password', {
                method: 'POST',
                body: JSON.stringify({
                    token,
                    newPassword: passwords.newPassword
                }),
            });
            setStatus('success');
            setTimeout(() => navigate('/login'), 3000);
        } catch (err) {
            setError(err.message || t('auth.reset_password_error', 'Error al restablecer la contraseña'));
            setStatus('error');
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setPasswords(prev => ({ ...prev, [name]: value }));
    };

    return (
        <div className="min-h-screen bg-slate-50 flex flex-col">
            <LandingNavbar />

            <div className="flex-1 flex flex-col justify-center items-center p-4 sm:p-8 lg:p-12 bg-white">
                <div className="w-full max-w-md space-y-8">
                    <div className="text-center space-y-2">
                        <h1 className="text-3xl font-extrabold text-slate-900">
                            {t('auth.reset_password_title', 'Restablecer Contraseña')}
                        </h1>
                        <p className="text-slate-500">
                            {status === 'success'
                                ? t('auth.reset_password_success_subtitle', 'Tu contraseña ha sido actualizada. Serás redirigido al inicio de sesión.')
                                : t('auth.reset_password_subtitle', 'Ingresa tu nueva contraseña para recuperar el acceso a tu cuenta')}
                        </p>
                    </div>

                    {status === 'success' ? (
                        <div className="space-y-6 animate-fade-in-up">
                            <div className="p-6 rounded-2xl bg-emerald-50 border border-emerald-100 text-emerald-700 flex flex-col items-center text-center gap-4">
                                <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600 text-2xl">
                                    <i className="fas fa-check-circle"></i>
                                </div>
                                <div>
                                    <h3 className="font-bold text-lg mb-1">{t('common.success', 'Éxito!')}</h3>
                                    <p className="text-sm opacity-90">
                                        {t('auth.password_reset_confirmation', 'Contraseña restablecida correctamente.')}
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={() => navigate('/login')}
                                className="w-full py-4 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-bold shadow-lg transition-all"
                            >
                                {t('auth.go_to_login', 'Ir al Inicio de Sesión')}
                            </button>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="space-y-6 mt-8">
                            <div className="space-y-2">
                                <label className="text-sm font-semibold text-slate-700 ml-1">
                                    {t('auth.new_password', 'Nueva Contraseña')}
                                </label>
                                <div className="relative">
                                    <input
                                        type="password"
                                        name="newPassword"
                                        className="w-full px-5 py-4 rounded-xl border-2 border-slate-200 text-lg bg-slate-50 focus:bg-white focus:border-blue-500 focus:ring-0 transition-all outline-none placeholder:text-slate-400"
                                        value={passwords.newPassword}
                                        onChange={handleChange}
                                        placeholder="••••••••"
                                        required
                                        autoFocus
                                        disabled={status === 'loading' || !token}
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="text-sm font-semibold text-slate-700 ml-1">
                                    {t('auth.confirm_password', 'Confirmar Contraseña')}
                                </label>
                                <div className="relative">
                                    <input
                                        type="password"
                                        name="confirmPassword"
                                        className="w-full px-5 py-4 rounded-xl border-2 border-slate-200 text-lg bg-slate-50 focus:bg-white focus:border-blue-500 focus:ring-0 transition-all outline-none placeholder:text-slate-400"
                                        value={passwords.confirmPassword}
                                        onChange={handleChange}
                                        placeholder="••••••••"
                                        required
                                        disabled={status === 'loading' || !token}
                                    />
                                </div>
                            </div>

                            {error && (
                                <div className="p-4 rounded-xl bg-red-50 border border-red-100 text-red-600 flex items-start gap-3 animate-fade-in-up">
                                    <i className="fas fa-exclamation-circle mt-0.5 shrink-0"></i>
                                    <p className="text-sm font-medium">{error}</p>
                                </div>
                            )}

                            <button
                                type="submit"
                                className="w-full py-4 rounded-xl bg-blue-600 hover:bg-blue-700 active:scale-[0.98] text-white font-bold text-lg shadow-lg hover:shadow-blue-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-3"
                                disabled={status === 'loading' || !token || !passwords.newPassword}
                            >
                                {status === 'loading' ? (
                                    <>
                                        <i className="fas fa-circle-notch fa-spin"></i>
                                        {t('common.updating', 'Actualizando...')}
                                    </>
                                ) : (
                                    <>
                                        <span>{t('auth.reset_password_button', 'Restablecer Contraseña')}</span>
                                        <i className="fas fa-save"></i>
                                    </>
                                )}
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};
